import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Paper,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { fetchJson } from '../../utils/fetchJson';

export default function StudentPromotionPage() {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [studentId, setStudentId] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleCheck = async () => {
    if (!studentId) {
      setError('Please enter a student ID');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const data = await fetchJson(`/api/promotion/student/${studentId}`, { method: 'GET' });
      setResult(data);
    } catch (err) {
      setError(err.message);
      setResult(null);
      enqueueSnackbar(err.message || 'Failed to load student promotion data', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Typography variant="h4" gutterBottom>
        {t('studentPromotion.title') || 'Student Promotion Check'}
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 3 }}>
        <TextField
          label="Student ID"
          value={studentId}
          onChange={(e) => setStudentId(e.target.value)}
          size="small"
        />
        <Button variant="contained" onClick={handleCheck} disabled={loading}>
          {loading ? <CircularProgress size={20} /> : 'Check Promotion'}
        </Button>
      </Box>

      {result && (
        <Paper sx={{ p: 2, maxWidth: 600 }}>
          <Typography variant="h6" gutterBottom>{result.studentName}</Typography>
          <Typography color="textSecondary" sx={{ mb: 2 }}>Class: {result.className}</Typography>

          <Table size="small" sx={{ mb: 2 }}>
            <TableHead>
              <TableRow>
                <TableCell>1st Term</TableCell>
                <TableCell>2nd Term</TableCell>
                <TableCell>3rd Term</TableCell>
                <TableCell>Overall</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>{(result.termAverages[1] ?? 0).toFixed?.(2) ?? '0.00'}</TableCell>
                <TableCell>{(result.termAverages[2] ?? 0).toFixed?.(2) ?? '0.00'}</TableCell>
                <TableCell>{(result.termAverages[3] ?? 0).toFixed?.(2) ?? '0.00'}</TableCell>
                <TableCell><strong>{result.overallAverage?.toFixed?.(2) ?? '0.00'}</strong></TableCell>
                <TableCell>
                  {result.promoted ? (
                    <Chip icon={<CheckCircleIcon />} label="Promoted" color="success" size="small" />
                  ) : (
                    <Chip icon={<CancelIcon />} label="Retained" color="error" size="small" />
                  )}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>

          {result.reasons && result.reasons.length > 0 && (
            <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>Promotion Criteria Evaluation:</Typography>
              {result.reasons.map((r, idx) => (
                <Box key={idx} sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  {r.passed ? <CheckCircleIcon color="success" sx={{ mr: 1, fontSize: 18 }} /> : <CancelIcon color="error" sx={{ mr: 1, fontSize: 18 }} />}
                  <Typography variant="body2">
                    <strong>{r.rule}:</strong> {r.details}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Paper>
      )}
    </Box>
  );
}
